/* Partner programme and team order pages (sections/pulse-partners.liquid, two modes). Specs:
   docs/superpowers/specs/2026-09-14-partner-program-design.md and 2026-09-15-team-orders-and-partner-form-design.md.

   Three Klaviyo client events, the plan's Event contract (docs/superpowers/plans/2026-09-14-partner-programme.md,
   "Amended 2026-09-15"): "Partner Application Submitted" (form v2), "Partner Video Submitted" and
   "Team Quote Requested". The Partners desk pulls them. NOTHING IS SENT UNTIL A SUBMIT: no Klaviyo call happens
   on render, and the marketing subscription is only made when its box is ticked. The page shows success whatever
   the honeypot says (the desk filters spam); only a failed network call shows the failure line.

   Market stamps on every event: pulse_market (the Klaviyo market code US CA UK AU NZ DE INTL, the value every flow
   splits on), pulse_market_handle (the Shopify market handle), pulse_country, pulse_domain, locale, currency,
   pulse_source, and partners_email: the market's partners address the page showed (sections/pulse-partners.liquid picks it
   per country), so a reply can be matched to the mailbox the applicant saw. The size value is always the US men's size;
   the Women's / Men's switch rewrites labels only.

   Pure builders first, for scripts/partners/partners.test.mjs (run in a DOM-less vm). */
(function (root) {
  'use strict';
  var FORM_VERSION = '2026-11-03';
  var REVISION = '2025-07-15';
  var METRIC_APPLY = 'Partner Application Submitted';
  var METRIC_VIDEO = 'Partner Video Submitted';
  var METRIC_TEAM = 'Team Quote Requested';
  var MAX_LINES = 40;
  var ROLES = ['dance', 'teach', 'school'];
  var TYPE_FOR_ROLE = { dance: 'creator', teach: 'instructor', school: 'school' };
  var FIELDS = {
    shared: ['first_name', 'last_name', 'city', 'owns_pair', 'website'],
    dance: ['ig_handle', 'tiktok_handle', 'youtube_handle', 'following_ig', 'following_tiktok', 'following_youtube', 'post_frequency', 'ideas'],
    teach: ['teach_where', 'students_per_week'],
    school: ['business_name', 'contact_role', 'students', 'group_buy']
  };
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var URL_RE = /^https?:\/\/\S+\.\S+/;

  function one(fd, name) { var v = fd.get(name); return v == null ? '' : String(v).trim(); }
  function many(fd, name) { return fd.getAll(name).map(function (v) { return String(v).trim(); }).filter(Boolean); }

  function normSize(v) {
    var s = v == null ? '' : String(v).trim();
    if (!/^\d{1,2}(\.\d+)?$/.test(s)) return '';
    return String(parseFloat(s));
  }

  function stamp(meta, source) {
    meta = meta || {};
    return {
      pulse_market: meta.market || '', pulse_market_handle: meta.marketHandle || '', pulse_country: meta.country || '',
      pulse_domain: meta.domain || '', locale: meta.locale || '', currency: meta.currency || '',
      pulse_source: source || 'partners', partners_email: meta.partnersEmail || ''
    };
  }

  function elapsed(meta) { return Math.max(0, Math.round((meta && meta.elapsedMs) || 0)); }

  function buildApplication(fd, meta) {
    var ticked = many(fd, 'roles');
    var roles = ROLES.filter(function (r) { return ticked.indexOf(r) >= 0; });
    var props = Object.assign(stamp(meta, 'partners'), {
      form_version: FORM_VERSION, roles: roles, applicant_type: roles.length ? TYPE_FOR_ROLE[roles[0]] : '',
      country: one(fd, 'country').toUpperCase(),
      styles: many(fd, 'styles'), video_links: many(fd, 'video_links').slice(0, 3),
      marketing_opt_in: fd.get('marketing_opt_in') != null,
      company_website: one(fd, 'company_website'),
      elapsed_ms: elapsed(meta)
    });
    FIELDS.shared.forEach(function (n) { props[n] = one(fd, n); });
    /* Dance and content always shows and is optional (Andre, 2026-11-03), so its answers travel for every role. */
    FIELDS.dance.forEach(function (n) { props[n] = one(fd, n); });
    props.dance_settings = many(fd, 'dance_settings');
    roles.forEach(function (r) { if (r !== 'dance') FIELDS[r].forEach(function (n) { props[n] = one(fd, n); }); });
    if (roles.indexOf('teach') >= 0) props.styles_taught = many(fd, 'styles_taught');
    return { email: one(fd, 'email').toLowerCase(), props: props };
  }

  function missing(app) {
    var p = app.props, out = [];
    if (!EMAIL_RE.test(app.email)) out.push('email');
    if (!p.first_name) out.push('first_name');
    if (!/^[A-Z]{2}$/.test(p.country)) out.push('country');
    if (!p.roles.length) out.push('roles');
    if (p.roles.indexOf('school') >= 0 && !p.business_name) out.push('business_name');
    return out;
  }

  function buildVideo(fd, meta) {
    return { email: one(fd, 'email').toLowerCase(),
             props: Object.assign(stamp(meta, 'partners'), { form_version: FORM_VERSION, video_url: one(fd, 'video_url'), platform: one(fd, 'platform') || 'other' }) };
  }

  function missingVideo(v) {
    var out = [];
    if (!EMAIL_RE.test(v.email)) out.push('email');
    if (!URL_RE.test(v.props.video_url)) out.push('video_url');
    return out;
  }

  function pairCount(lines) { return (lines || []).reduce(function (n, l) { return n + (parseInt(l.qty, 10) > 0 ? parseInt(l.qty, 10) : 0); }, 0); }
  function lineHtml(template, n) { return String(template).split('__N__').join(String(n)); }

  function buildTeamRequest(fd, meta, indices) {
    var follow = fd.get('sizes_to_follow') != null;
    var lines = (indices || []).slice(0, MAX_LINES).map(function (n) {
      return { style_handle: one(fd, 'line_' + n + '_style'), size_usm: normSize(one(fd, 'line_' + n + '_size')),
               qty: parseInt(one(fd, 'line_' + n + '_qty'), 10) || 0 };
    }).filter(function (l) { return l.qty > 0 && (l.style_handle || l.size_usm); });
    var props = Object.assign(stamp(meta, 'team-order'), {
      form_version: FORM_VERSION, org_name: one(fd, 'org_name'), contact_name: one(fd, 'contact_name'), phone: one(fd, 'phone'),
      delivery_country: one(fd, 'delivery_country').toUpperCase(), needed_by: one(fd, 'needed_by'), lines: lines,
      sizes_to_follow: follow, declared_pairs: follow ? (parseInt(one(fd, 'declared_pairs'), 10) || 0) : null,
      notes: one(fd, 'notes'), company_website: one(fd, 'company_website'), elapsed_ms: elapsed(meta)
    });
    return { email: one(fd, 'email').toLowerCase(), props: props };
  }

  function missingTeam(req, minPairs) {
    var p = req.props, out = [];
    if (!EMAIL_RE.test(req.email)) out.push('email');
    if (!p.org_name) out.push('org_name');
    if (!p.contact_name) out.push('contact_name');
    if (!/^[A-Z]{2}$/.test(p.delivery_country)) out.push('delivery_country');
    if (!/^(\d{4}-\d{2}-\d{2})?$/.test(p.needed_by)) out.push('needed_by');
    if (p.sizes_to_follow) {
      if (!(p.declared_pairs >= minPairs)) out.push('declared_pairs');
    } else if (!p.lines.length || p.lines.some(function (l) { return !l.style_handle || !l.size_usm; }) || pairCount(p.lines) < minPairs) {
      out.push('lines');
    }
    return out;
  }

  function countryCode(name, options) {
    var want = String(name || '').trim().toLowerCase();
    for (var i = 0; i < (options || []).length; i++) {
      if (String(options[i].text || '').trim().toLowerCase() === want) return options[i].code || '';
    }
    return '';
  }

  function post(url, body) {
    return { url: url, init: { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', 'revision': REVISION }, body: JSON.stringify(body) } };
  }

  function profileProps(props) {
    var out = {};
    ['pulse_market', 'pulse_market_handle', 'pulse_country', 'pulse_domain', 'locale', 'currency', 'pulse_source'].forEach(function (k) { out[k] = (props && props[k]) || ''; });
    return out;
  }

  function eventRequest(company, metric, email, props) {
    return post('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(company), { data: { type: 'event', attributes: {
      properties: props,
      metric: { data: { type: 'metric', attributes: { name: metric } } },
      profile: { data: { type: 'profile', attributes: { email: email, properties: profileProps(props) } } } } } });
  }

  function subscriptionRequest(company, list, email, props) {
    return post('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), { data: { type: 'subscription',
      attributes: { custom_source: 'Partner application',
        profile: { data: { type: 'profile', attributes: { email: email, properties: profileProps(props),
          subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } },
      relationships: { list: { data: { type: 'list', id: list } } } } });
  }

  function countMessage(copy, n, minPairs, later) {
    if (later) return copy.later;
    if (n >= minPairs) return copy.met;
    return String(copy.short || '').split('[count]').join(String(minPairs - n));   /* stored as [count]: see apply_copy.py PLACEHOLDERS */
  }

  root.PulsePartners = {
    FORM_VERSION: FORM_VERSION, METRIC_APPLY: METRIC_APPLY, METRIC_VIDEO: METRIC_VIDEO, METRIC_TEAM: METRIC_TEAM,
    MAX_LINES: MAX_LINES, ROLES: ROLES, normSize: normSize, stamp: stamp, buildApplication: buildApplication,
    missing: missing, buildVideo: buildVideo, missingVideo: missingVideo, pairCount: pairCount, lineHtml: lineHtml,
    buildTeamRequest: buildTeamRequest, missingTeam: missingTeam, countryCode: countryCode, eventRequest: eventRequest,
    subscriptionRequest: subscriptionRequest, countMessage: countMessage
  };
  if (typeof document === 'undefined') return;

  /* ------------------------------------------------------------------------------------------------ DOM */

  function $$(sel, el) { return Array.prototype.slice.call((el || document).querySelectorAll(sel)); }
  function send(req) { return fetch(req.url, req.init).catch(function () { return { ok: false }; }); }

  /* The country combobox: the contact page's behaviour (sections/pulse-contact.liquid), on its own data-ppc-combo
     hooks, committing the ISO code to the hidden [data-ppc-combo-code] input the builders read. */
  function combo(rootEl) {
    var input = rootEl.querySelector('[data-ppc-combo-input]');
    var list = rootEl.querySelector('[data-ppc-combo-list]');
    var code = rootEl.querySelector('[data-ppc-combo-code]');
    var err = rootEl.querySelector('[data-ppc-combo-err]');
    var opts = $$('[role="option"]', list);
    var active = -1, visible = [];
    function norm(s) { return String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }
    function setActive(i) {
      if (active >= 0 && visible[active]) visible[active].classList.remove('is-active');
      active = i;
      if (active >= 0 && visible[active]) {
        var o = visible[active];
        o.classList.add('is-active');
        input.setAttribute('aria-activedescendant', o.id);
        if (o.offsetTop < list.scrollTop) list.scrollTop = o.offsetTop;
        else if (o.offsetTop + o.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = o.offsetTop + o.offsetHeight - list.clientHeight;
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }
    function filter(q) {
      q = norm(q.trim());
      var starts = [], words = [], within = [];
      opts.forEach(function (o) {
        var n = norm(o.textContent);
        if (!q || n.indexOf(q) === 0) starts.push(o);
        else if (n.split(/[\s-]+/).some(function (w) { return w.indexOf(q) === 0; })) words.push(o);
        else if (n.indexOf(q) >= 0) within.push(o);
      });
      visible = starts.concat(words, within);
      opts.forEach(function (o) { o.hidden = visible.indexOf(o) < 0; o.classList.remove('is-active'); });
      visible.forEach(function (o) { list.appendChild(o); });
      setActive(visible.length && q ? 0 : -1);
    }
    function open() { list.hidden = false; input.setAttribute('aria-expanded', 'true'); }
    function close() { list.hidden = true; input.setAttribute('aria-expanded', 'false'); setActive(-1); }
    function commit(o) {
      input.value = o.textContent.trim();
      code.value = o.getAttribute('data-code') || '';
      input.removeAttribute('aria-invalid');
      err.hidden = true;
      close();
    }
    function settle() {
      var q = norm(input.value.trim());
      var hit = opts.filter(function (o) { return norm(o.textContent.trim()) === q; })[0];
      if (hit) commit(hit); else { code.value = ''; close(); }
    }
    input.addEventListener('focus', function () { filter(''); open(); });
    input.addEventListener('click', function () { if (list.hidden) { filter(''); open(); } });
    input.addEventListener('input', function () { code.value = ''; filter(input.value); open(); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); if (list.hidden) { filter(input.value); open(); } setActive(Math.min(active + 1, visible.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(active - 1, 0)); }
      else if (e.key === 'Enter') { if (!list.hidden) { e.preventDefault(); if (active >= 0) commit(visible[active]); else settle(); } }
      else if (e.key === 'Escape') { if (!list.hidden) { e.preventDefault(); close(); } }
      else if (e.key === 'Tab') { settle(); }
    });
    list.addEventListener('mousedown', function (e) { e.preventDefault(); });
    list.addEventListener('click', function (e) { var o = e.target.closest('[role="option"]'); if (o) commit(o); });
    document.addEventListener('click', function (e) { if (!rootEl.contains(e.target) && !list.hidden) settle(); });
    return { settle: settle };
  }

  /* The ONE style / scale / size control. The scale radios rewrite the option text from data-w / data-m; the
     option VALUE (US men's) never changes. The first scale shown is the site's own (window.PulseSizeScale). */
  function pick(el) {
    if (!el || el.__ppcPick) return;
    el.__ppcPick = true;
    var size = el.querySelector('[data-ppc-size]');
    function relabel() {
      var checked = el.querySelector('[data-ppc-scale]:checked');
      var sc = checked && checked.value === 'm' ? 'm' : 'w';
      $$('option', size).forEach(function (o) { if (o.value) o.textContent = o.getAttribute('data-' + sc); });
    }
    var start = 'w';
    try { if (root.PulseSizeScale && root.PulseSizeScale.get() === 'm') start = 'm'; } catch (e) {}
    $$('[data-ppc-scale]', el).forEach(function (r) { r.checked = r.value === start; });
    el.addEventListener('change', function (e) {
      if (!e.target.hasAttribute('data-ppc-scale')) return;
      try { if (root.PulseSizeScale) root.PulseSizeScale.set(e.target.value); } catch (err) {}
      relabel();
    });
    relabel();
  }

  function flag(form, names) {
    $$('[aria-invalid]', form).forEach(function (el) { el.removeAttribute('aria-invalid'); });
    $$('.is-invalid', form).forEach(function (el) { el.classList.remove('is-invalid'); });
    $$('[data-err-for]', form).forEach(function (el) { el.hidden = names.indexOf(el.getAttribute('data-err-for')) < 0; });
    var first = null;
    names.forEach(function (n) {
      var el = form.querySelector('[data-flag="' + n + '"]') || form.querySelector('[name="' + n + '"]:not([type=hidden])');
      if (!el) return;
      if (/^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) el.setAttribute('aria-invalid', 'true'); else el.classList.add('is-invalid');
      if (!first) first = el;
    });
    if (first) {
      first.focus({ preventScroll: true });
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function finish(form, ok) {
    var okEl = form.querySelector('[data-ppc-ok]'), failEl = form.querySelector('[data-ppc-fail]'), fields = form.querySelector('[data-ppc-fields]');
    okEl.hidden = !ok; failEl.hidden = ok;
    if (ok && fields) fields.hidden = true;
    var msg = ok ? okEl : failEl;
    msg.focus({ preventScroll: true });
    msg.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function init(section) {
    var mode = section.getAttribute('data-ppc-mode');
    var company = section.getAttribute('data-klaviyo-id'), list = section.getAttribute('data-list');
    var minPairs = parseInt(section.getAttribute('data-min-pairs'), 10) || 5;
    var meta = { market: section.getAttribute('data-market'), marketHandle: section.getAttribute('data-market-handle'),
                 country: section.getAttribute('data-country'), domain: section.getAttribute('data-domain'),
                 locale: section.getAttribute('data-locale'), currency: section.getAttribute('data-currency'),
                 partnersEmail: section.getAttribute('data-partners-email') };
    function withElapsed(started) { return Object.assign({}, meta, { elapsedMs: started ? Date.now() - started : 0 }); }
    $$('[data-ppc-combo]', section).forEach(combo);
    $$('[data-ppc-pick]', section).forEach(pick);

    /* the menu rows: one open at a time is not required, but a question row inside the FAQ stays independent */

    if (mode === 'team') initTeam(); else { initApply(); initVideo(); }
    initRail();

    /* the partner video rail: the arrows page the track two cards at a time; the cards themselves open the
       shared viewer (snippets/pulse-video-viewer, [data-dvcard]) */
    function initRail() {
      var track = section.querySelector('[data-ppc-vids-track]');
      if (!track) return;
      function step(dir) {
        var card = track.querySelector('li');
        var w = card ? card.getBoundingClientRect().width + 10 : track.clientWidth / 2;
        track.scrollBy({ left: dir * w * 2, behavior: 'smooth' });
      }
      var prev = section.querySelector('[data-ppc-vids-prev]'), next = section.querySelector('[data-ppc-vids-next]');
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
    }

    function initApply() {
      var form = section.querySelector('[data-ppc-apply]');
      if (!form) return;
      var started = 0;
      function groups() {
        var ticked = $$('[name=roles]:checked', form).map(function (i) { return i.value; });
        $$('[data-role]', form).forEach(function (el) {
          var on = ticked.indexOf(el.getAttribute('data-role')) >= 0;
          el.hidden = !on;
          $$('input, select, textarea', el).forEach(function (i) { i.disabled = !on; });
        });
        if (ticked.length) $$('[data-flag=roles]', form).forEach(function (el) { el.classList.remove('is-invalid'); });
        $$('[data-reveal]', form).forEach(function (el) {
          var c = form.querySelector('[name="' + el.getAttribute('data-reveal') + '"]:checked');
          var on = !!c && !c.disabled && c.value === 'yes';
          el.hidden = !on;
          $$('input, select, textarea', el).forEach(function (i) { i.disabled = !on; });
        });
      }
      form.addEventListener('change', function (e) {
        if (['roles', 'group_buy'].indexOf(e.target.name) >= 0) groups();
        if (e.target.getAttribute('aria-invalid')) e.target.removeAttribute('aria-invalid');
      });
      form.addEventListener('focusin', function () { if (!started) started = Date.now(); });
      groups();
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var app = buildApplication(new FormData(form), withElapsed(started));
        var gaps = missing(app);
        if (gaps.length) {
          flag(form, gaps);
          return;
        }
        var btn = form.querySelector('[type=submit]');
        btn.disabled = true;
        if (app.props.marketing_opt_in) send(subscriptionRequest(company, list, app.email, app.props));
        send(eventRequest(company, METRIC_APPLY, app.email, app.props)).then(function (res) {
          btn.disabled = !!(res && res.ok);
          finish(form, !!(res && res.ok));
        });
      });
    }

    function initVideo() {
      var form = section.querySelector('[data-ppc-video]');
      if (!form) return;
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var v = buildVideo(new FormData(form), meta);
        var gaps = missingVideo(v);
        if (gaps.length) { flag(form, gaps); return; }
        var btn = form.querySelector('[type=submit]');
        btn.disabled = true;
        send(eventRequest(company, METRIC_VIDEO, v.email, v.props)).then(function (res) {
          btn.disabled = false;
          if (res && res.ok) form.reset();
          finish(form, !!(res && res.ok));
        });
      });
    }

    function initTeam() {
      var form = section.querySelector('[data-ppc-team]');
      if (!form) return;
      var tpl = section.querySelector('template[data-ppc-line-tpl]').innerHTML;
      var box = form.querySelector('[data-ppc-lines]');
      var later = form.querySelector('[data-ppc-later]');
      var laterBox = form.querySelector('[data-ppc-later-box]');
      var total = form.querySelector('[name=declared_pairs]');
      var counter = form.querySelector('[data-ppc-count]');
      var addBtn = form.querySelector('[data-ppc-add]');
      var copy = { one: counter.getAttribute('data-one'), many: counter.getAttribute('data-many'), short: counter.getAttribute('data-short'),
                   met: counter.getAttribute('data-met'), later: counter.getAttribute('data-later'), error: counter.getAttribute('data-error') };
      var next = 1, started = 0;

      function indices() { return $$('[data-ppc-line]', box).map(function (el) { return Number(el.getAttribute('data-n')); }); }
      function linePairs() { return pairCount($$('[data-ppc-qty]', box).map(function (i) { return { qty: i.value }; })); }
      function renumber() {
        var all = $$('[data-ppc-line]', box);
        all.forEach(function (el, i) {
          el.querySelector('[data-ppc-line-n]').textContent = (i < 9 ? '0' : '') + (i + 1);
          el.querySelector('[data-ppc-remove]').hidden = all.length < 2;
        });
        addBtn.hidden = all.length >= MAX_LINES;
      }
      function count(error) {
        var on = later.checked;
        var n = on ? (parseInt(total.value, 10) || 0) : linePairs();
        counter.querySelector('[data-ppc-count-n]').textContent = n;
        counter.querySelector('[data-ppc-count-word]').textContent = n === 1 ? copy.one : copy.many;
        counter.querySelector('[data-ppc-count-bar]').style.width = Math.min(100, n / minPairs * 100) + '%';
        counter.classList.toggle('is-met', n >= minPairs);
        counter.classList.toggle('is-error', !!error);
        counter.querySelector('[data-ppc-count-msg]').textContent = error ? copy.error : countMessage(copy, n, minPairs, on && n < minPairs);
      }
      function follow() {
        var on = later.checked;
        laterBox.hidden = !on;
        total.disabled = !on;
        $$('[data-ppc-style], [data-ppc-size]', box).forEach(function (s) { s.required = !on; });
      }
      function addLine() {
        if (indices().length >= MAX_LINES) return null;
        box.insertAdjacentHTML('beforeend', lineHtml(tpl, next));
        next += 1;
        var el = box.lastElementChild;
        pick(el.querySelector('[data-ppc-pick]'));
        follow(); renumber(); count();
        return el;
      }

      form.addEventListener('focusin', function () { if (!started) started = Date.now(); });
      addBtn.addEventListener('click', function () { var el = addLine(); if (el) el.querySelector('select').focus(); });
      box.addEventListener('click', function (e) {
        var rm = e.target.closest('[data-ppc-remove]');
        if (!rm) return;
        var line = rm.closest('[data-ppc-line]'), sib = line.previousElementSibling || line.nextElementSibling;
        if (indices().length > 1) line.remove();
        renumber(); count();
        if (sib) { var f = sib.querySelector('select'); if (f) f.focus(); }
      });
      form.addEventListener('input', function () { count(); });
      form.addEventListener('change', function (e) {
        if (e.target === later) follow();
        if (e.target.getAttribute('aria-invalid')) e.target.removeAttribute('aria-invalid');
        count();
      });
      addLine();

      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var req = buildTeamRequest(new FormData(form), withElapsed(started), indices());
        var gaps = missingTeam(req, minPairs);
        if (gaps.length) {
          var names = gaps.slice();
          if (gaps.indexOf('lines') >= 0) {
            count(true);
            if (!req.props.sizes_to_follow) {
              /* point at the first incomplete line control rather than the counter when one exists */
              var blank = $$('[data-ppc-style], [data-ppc-size]', box).filter(function (s) { return !s.value; })[0];
              if (blank) { names.splice(names.indexOf('lines'), 1, blank.name); }
            }
          }
          if (gaps.indexOf('declared_pairs') >= 0) count(true);
          flag(form, names);
          return;
        }
        var btn = form.querySelector('[type=submit]');
        btn.disabled = true;
        send(eventRequest(company, METRIC_TEAM, req.email, req.props)).then(function (res) {
          btn.disabled = !!(res && res.ok);
          finish(form, !!(res && res.ok));
        });
      });
    }
  }

  function start() { $$('[data-ppc]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})(typeof window !== 'undefined' ? window : globalThis);
